// Diagnostic placement. Two variable-length (8-12 item) reversal-stop
// adaptive staircases — math, then reading — each stepped live through the
// shared adaptiveStaircase.ts reducer (ADR-058). Completion flips
// kids.diagnostic_done = true and account onboarding_state to COMPLETE so the
// resolver routes to /play.
//
// MA-155 (math-diagnostic-screen-wiring, ADR-084): the math phase now runs
// mathDiagnosticStaircase.ts's real-module-grounded 4-rung ladder through the
// staircase reducer, replacing the old fixed 5-item diagnosticPlacement.ts
// bank (MA-64) — the "math" half of MA-155's own acceptance sentence ("math +
// reading" both variable-length staircases). diagnosticPlacement.ts itself is
// left as production-dead code this slice (three test files still pin its
// frozen shape as zero-regression guards); its removal is a named follow-up,
// not done here.
//
// MA-155 (reading-diagnostic-screen-wiring, ADR-082): after the math phase, a
// second variable-length (8-12 item) reversal-stop READING phase runs (real
// content via readingDiagnosticContent.ts). `finish()` is reachable from
// exactly one place — the reading phase's staircase-done branch.

import { useState, useMemo, useRef, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAccount, useKids } from "@/lib/account";
import { useAppState } from "@/App/AppStateContext.tsx";
import { TOTAL_KID_STEPS } from "@/lib/kidOnboarding";
import {
  MATH_DIAGNOSTIC_STAIRCASE_LADDER,
  mathDiagnosticStaircaseItemAt,
  mathDiagnosticStaircaseChoices,
  type MathDiagnosticStaircaseItemResult,
} from "@/engine/mathDiagnosticStaircase";
import {
  READING_DIAGNOSTIC_LADDER,
  readingDiagnosticItemAt,
  readingDiagnosticChoices,
  type ReadingDiagnosticItemResult,
} from "@/engine/readingDiagnosticContent";
import { insertPlacementResult } from "@/supabaseClient.js";
import {
  initDiagnosticStrand,
  stepDiagnosticStrand,
  type DiagnosticStrandRun,
} from "@/lib/engine/diagnosticStart";
import { onboardingDiagnosticPatch, onboardingDiagnosticResults } from "@/lib/engine/onboardingDiagnosticPatch";
import { readPendingCheckToken, clearPendingCheckToken } from "@/lib/check/pendingCheckToken";
import { checkPrefillUsable, checkPrefillResults, checkPrefillPatch } from "@/lib/check/checkPrefill";
import { verifyCheckTokenRemote, type VerifiedCheckToken } from "@/screens/readinessCheck/checkTokenClient";

export const Route = createFileRoute("/onboarding/diagnostic")({
  head: () => ({ meta: [{ title: "PencilWise · Quick check-in" }] }),
  component: Diagnostic,
});

// H-A4 shared step styles (H-0 tokens) — see onboarding.kid.name.tsx.
const EYEBROW = "text-xs font-bold uppercase tracking-[0.18em] text-terracotta-deep";

function Diagnostic() {
  const navigate = useNavigate();
  const app = useAppState();
  const { data: account } = useAccount();
  const { data: kids = [] } = useKids();
  const kid = kids[kids.length - 1] ?? null;
  const seed = useMemo(() => Date.now() % 7, []);
  const [busy, setBusy] = useState(false);
  // Synchronous re-entrancy lock (distinct from `busy` state, which only
  // drives the disabled-button render prop). A double-tap fires two click
  // events; React state updates don't take effect until the next render, so
  // a ref check is what actually prevents a second tap in the SAME tick from
  // stepping a staircase twice for one answer (MA-155 red-team finding —
  // each stepStaircase call must be exactly once-per-answer, or the
  // persisted placement seed is corrupted). Shared across both phases.
  const busyRef = useRef(false);

  // MA-155: math phase state (ADR-084) — a variable-length (8-12 item)
  // reversal-stop staircase, same mechanism the reading phase below uses.
  // `finalMathState` captures the staircase's terminal state at the exact
  // moment the math phase hands off to reading (needed by finish(), which
  // runs later, after the reading phase's own staircase also completes).
  // MA-1120 (D-1025-2): each strand is built and stepped by the SAME shared
  // functions the free readiness check uses (diagnosticStart.ts), seeded at the
  // child's age rung — so the two can never disagree on a start. The age comes
  // from the kid row (the onboarding age step), so the math run is created once
  // the kid is known.
  const [phase, setPhase] = useState<"math" | "reading">("math");
  const seedAge = ((kid as { age?: number | null } | null)?.age ?? (app?.progress as { studentAge?: number | null } | undefined)?.studentAge ?? null) as number | null;
  const [mathRun, setMathRun] = useState<DiagnosticStrandRun | null>(null);
  const [finalMathRun, setFinalMathRun] = useState<DiagnosticStrandRun | null>(null);
  const mathItemsRef = useRef<MathDiagnosticStaircaseItemResult[]>([]);
  const mathState = mathRun?.state ?? null;
  // (RED r1 N2) The kid row carries the age the onboarding age step wrote, so
  // it is known by the time the check-in mounts; with no age anywhere the
  // shared seed treats the child as 6 (diagnosticSeedRung), the same default
  // the age model uses. The run is seeded once and never re-seeded mid-run.
  useEffect(() => {
    if (kid && !mathRun && !finalMathRun) setMathRun(initDiagnosticStrand("math", seedAge));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kid?.id]);

  // MA-155: reading phase state. `readingRun` stays null until the math
  // phase's last item transitions into it via onPickMath's done branch.
  const [readingRun, setReadingRun] = useState<DiagnosticStrandRun | null>(null);
  const readingState = readingRun?.state ?? null;
  const readingItemsRef = useRef<ReadingDiagnosticItemResult[]>([]);

  // MA-1044: a finished free readiness check (/check) is reused instead of a
  // second check-in. The token (captured from /onboarding?check= at app start)
  // is verified by the server first; only a COMPLETE check for this child's
  // age band is used (checkPrefillUsable). Anything else — no token, a bad or
  // expired one, a partial run, another age — runs the check-in below as
  // before. The child id is captured before the wait, like finish().
  const [checkPrefill, setCheckPrefill] = useState<"checking" | "none">(() =>
    readPendingCheckToken() ? "checking" : "none",
  );
  useEffect(() => {
    if (checkPrefill !== "checking" || !kid || !account) return;
    const token = readPendingCheckToken();
    if (!token) {
      setCheckPrefill("none");
      return;
    }
    const childId = (app?.getLiveActiveChildId?.() ?? app?.activeChildId ?? null) as string | null;
    const kidAge = (kid as { age?: number | null }).age;
    let live = true;
    const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 6000));
    void Promise.race([verifyCheckTokenRemote(token), timeout]).then((payload) => {
      // Only the live run consumes the token: a cancelled earlier run (the
      // effect re-ran) must not clear it under the run that will use it.
      if (!live) return;
      clearPendingCheckToken();
      if (!payload || !checkPrefillUsable(payload, kidAge)) {
        setCheckPrefill("none");
        return;
      }
      void applyReadinessCheck(childId, payload);
    });
    return () => {
      live = false;
    };
    // Keyed on ids, not object identity: kids/account are re-derived on
    // renders, and a re-run would restart the verify (RED r1 SHOULD-7).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkPrefill, kid?.id, account?.id]);

  const applyReadinessCheck = async (childId: string | null, payload: VerifiedCheckToken) => {
    setBusy(true);
    app?.setProgressForChild?.(childId, (prev: Record<string, unknown>) => checkPrefillPatch(prev, payload));
    const sid = app?.ensureActiveStudentId ? await app.ensureActiveStudentId() : null;
    if (sid) {
      // Same cloud mirror as finish() (I.7): one row per strand, same source.
      const { result, readingResult } = checkPrefillResults(payload);
      void insertPlacementResult({ studentId: sid, result, source: "onboarding_diagnostic" });
      void insertPlacementResult({ studentId: sid, result: readingResult, source: "onboarding_diagnostic" });
    }
    navigate({ to: "/onboarding/parent-results" });
  };

  if (!kid || checkPrefill === "checking") {
    return (
      <div className="grid min-h-screen place-items-center bg-cream">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  const mathItem =
    phase === "math" && mathState ? mathDiagnosticStaircaseItemAt(mathState.rung, mathState.itemsServed) : null;
  const mathChoices = mathItem && mathState ? mathDiagnosticStaircaseChoices(mathItem, seed + mathState.itemsServed) : [];

  const readingItem =
    phase === "reading" && readingState
      ? readingDiagnosticItemAt(readingState.rung, readingState.itemsServed)
      : null;
  const readingChoices =
    readingItem && readingState
      ? readingDiagnosticChoices(readingItem, seed + readingState.itemsServed)
      : [];

  const finish = async (finalReadingRun: DiagnosticStrandRun) => {
    if (!account || !kid || !finalMathRun) return;
    setBusy(true);
    // Track I.1.a: persist the diagnostic result instead of discarding it.
    // Mastery is still owned by the adaptation engine (child_attempts →
    // useSkillGraph); this captures the placement SEED the engine can later
    // use (I.1.b consumes it). The result is stored locally on
    // progress.placementResults AND mirrored to the cloud placement_results
    // table (I.7) keyed on the canonical cloud students.id.
    // MA-155 (ADR-084): now the math staircase's own result builder — see
    // `finalMathRun`, captured by onPickMath's done branch above.
    // MA-1120 (RED r1 M1): the stored result carries the PLACEMENT rung (the
    // module the start rule writes below), exactly as the readiness-check
    // prefill stores it, so MA-784's restore and the evidence floor read back
    // the module that was written.
    // MA-1171: built by the exported helper the tests call (onboardingDiagnosticPatch.ts).
    const { result, readingResult } = onboardingDiagnosticResults(mathItemsRef.current, finalMathRun, readingItemsRef.current, finalReadingRun);
    // MA-155: the reading-phase result (real content + real staircase,
    // ADR-081/ADR-058). Stored on a SEPARATE progress field
    // (readingPlacementResults) — applyPlacementBias's one caller hardcodes
    // strand:"math" and onboarding.parent-results.tsx has no reading-strand
    // consumer surface yet, so wiring this into the existing bias flow is
    // deliberately out of scope for this slice.
    // MA-842: the child this result is FOR, captured before the wait below
    // (the live child now, else the render's). The write goes to this id
    // only (setProgressForChild); with no child at all it is React-only.
    const childId = (app?.getLiveActiveChildId?.() ?? app?.activeChildId ?? null) as string | null;
    const sid = app?.ensureActiveStudentId ? await app.ensureActiveStudentId() : null;
    if (sid) {
      // Fire-and-forget cloud mirror (I.7). Offline / unlinked kid (sid null)
      // → skip the cloud write; local progress below still persists, and the
      // next sign-in links the row (one-way cloud→local mirror deferral).
      // Both inserts share the DB `source` column's constrained value
      // ("onboarding_diagnostic" — placement_results_source_check only
      // allows onboarding_diagnostic/parent_set/replacement); the reading
      // result's own finer-grained tag ("reading_diagnostic_staircase")
      // lives inside the JSON `result` payload, not the column, so no
      // migration is needed for this slice.
      void insertPlacementResult({ studentId: sid, result, source: "onboarding_diagnostic" });
      void insertPlacementResult({ studentId: sid, result: readingResult, source: "onboarding_diagnostic" });
    }
    // MA-31: kids/account are useMemo derivations, not react-query
    // producers — the invalidations here were no-ops, removed.
    // Persist locally + keep placementStatus:"complete" so the diagnostic's
    // own result sticks (MA-155 removed the legacy needsPlacement gate this
    // used to also guard against, as a confirmed dead end — see App.jsx).
    // (prev annotation REQUIRED — strict/noImplicitAny.)
    app?.setProgressForChild?.(childId, (prev: Record<string, unknown>) =>
      // MA-301: derive the evidence-backed placement floor from each
      // staircase's terminal rung — "raises, never lowers" the module id
      // the ranker's placementFloor() reads, so a strong diagnostic result
      // stops routing behind the age-default floor written at onboarding
      // creation (App.jsx). Read from `prev` (not the outer `app.progress`
      // closure) so this can never race a concurrent progress write.
      // MA-1044: one shared function (diagnosticStart.ts) so the free
      // readiness check names exactly the start this writes.
      // MA-1120: seeded strands; a run that placed below its age rung lowers.
      // MA-775: a strand the staircase MOVED (raised, or MA-1120 lowered
      // below the age rung) is evidence ("diagnostic"); a strand it did not
      // move keeps its record (still the age seed).
      // MA-1171: the whole patch is one exported helper, the one the tests call.
      onboardingDiagnosticPatch(prev, finalMathRun, finalReadingRun, { result, readingResult }),
    );
    // MA-1044: a check-in finished here leaves no readiness-check token behind
    // to prefill a sibling of the same age band later.
    clearPendingCheckToken();
    navigate({ to: "/onboarding/parent-results" });
  };

  const onPickMath = (n: number) => {
    if (busyRef.current || !mathItem || !mathRun || !mathState) return;
    busyRef.current = true;
    const wasRight = n === mathItem.correctAnswer;
    // Track I.1.a: record this item's outcome BEFORE advancing/finishing, so
    // the last item is captured before finish() reads the ref.
    mathItemsRef.current.push({
      moduleId: MATH_DIAGNOSTIC_STAIRCASE_LADDER[mathState.rung].moduleId,
      itemId: mathItem.id,
      chosen: n,
      correct: wasRight,
    });
    const next = stepDiagnosticStrand(mathRun, wasRight);
    if (next.state.done) {
      // MA-155: the math phase's staircase-done branch hands off to the
      // reading phase instead of calling finish() directly — finish() is
      // reachable ONLY from onPickReading's staircase-done branch below.
      setFinalMathRun(next);
      setMathRun(null);
      setPhase("reading");
      setReadingRun(initDiagnosticStrand("reading", seedAge));
    } else {
      setMathRun(next);
    }
    busyRef.current = false;
  };

  const onPickReading = (chosen: string) => {
    if (busyRef.current || !readingRun || !readingState || !readingItem) return;
    busyRef.current = true;
    const wasRight = chosen === readingItem.correctAnswer;
    readingItemsRef.current.push({
      skillId: READING_DIAGNOSTIC_LADDER[readingState.rung].skillId,
      itemId: readingItem.id,
      chosen,
      correct: wasRight,
    });
    const next = stepDiagnosticStrand(readingRun, wasRight);
    if (next.state.done) {
      void finish(next);
    } else {
      setReadingRun(next);
      busyRef.current = false;
    }
  };

  return (
    <div className="relative min-h-screen bg-cream paper-grain">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
        <header className="pointer-events-none animate-slide-up">
          <p className={EYEBROW}>
            Step {TOTAL_KID_STEPS} of {TOTAL_KID_STEPS} · Quick check-in
          </p>
          {phase === "math" ? (
            // MA-155 (ADR-084): the math phase is now variable-length (8-12
            // items, reversal-stop) too — no fixed denominator to show
            // honestly, mirroring the reading phase's own copy below.
            <h1 className="mt-2 font-serif text-2xl font-bold tracking-tight">Math check-in</h1>
          ) : (
            <h1 className="mt-2 font-serif text-2xl font-bold tracking-tight">Reading check-in</h1>
          )}
        </header>

        {phase === "math" && mathItem ? (
          <>
            {/* NB e2e contract (18/19/20): the prompt must stay
                `p.font-serif.text-5xl` and the answers must stay
                `.grid-cols-3 button`. */}
            <div className="mt-8 rounded-3xl border border-border/60 bg-card p-8 text-center shadow-soft">
              <p className="font-serif text-5xl font-bold tracking-wide">{mathItem.prompt}</p>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {mathChoices.map((c) => (
                  <button
                    key={c}
                    onClick={() => onPickMath(c)}
                    disabled={busy}
                    className="min-h-[64px] rounded-2xl border-2 border-border bg-background px-4 py-5 font-kid text-2xl font-extrabold shadow-soft transition-all hover:border-primary active:scale-[0.97] active:border-primary disabled:opacity-50"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-6 text-center text-xs text-muted-foreground">
              No pressure — guess if you're not sure.
            </p>
          </>
        ) : readingItem ? (
          <>
            {/* NB e2e contract (18/19/20 MA-155 update): the reading tiles
                keep the SAME `.grid-cols-3 button` shape as the math phase —
                only the loop bound changes (variable-length; e2e stops on
                navigation to /onboarding/parent-results, not a fixed count). */}
            <div className="mt-8 rounded-3xl border border-border/60 bg-card p-8 text-center shadow-soft">
              <p className="font-serif text-2xl font-bold tracking-wide">{readingItem.prompt}</p>
              <div className="mt-6 grid grid-cols-3 gap-3">
                {readingChoices.map((c) => (
                  <button
                    key={c}
                    onClick={() => onPickReading(c)}
                    disabled={busy}
                    className="min-h-[64px] rounded-2xl border-2 border-border bg-background px-4 py-5 font-kid text-xl font-extrabold shadow-soft transition-all hover:border-primary active:scale-[0.97] active:border-primary disabled:opacity-50"
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-6 text-center text-xs text-muted-foreground">
              Just a few more — we're finding the right reading level.
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
}
